import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { InputText } from "primereact/inputtext";
import { Password } from 'primereact/password';
import { Checkbox } from "primereact/checkbox";
import { Button } from 'primereact/button';
import { Dialog } from "primereact/dialog";
import { Ripple } from "primereact/ripple";

import { checkEmail, checkText } from "../../Utils/Validator";
import { loginService, newUserUserTypeAdd } from "../../Api/SignupApi";
import { useAuth } from "../../Authentication/AuthContext";
import { Toast } from "primereact/toast";
import { goToTop } from "../../Components/goToTop";

import { Fade, Slide, Zoom } from "react-awesome-reveal";
import { appStoreLink, playStoreLink } from "../../../Website/Utils/Info";

import { fetchUserTypes } from "../../Controllers/Thunks/plansThunks";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";

import { UserType } from "../../Controllers/Types/UserTypeInterface";
import { baseURL } from "../../Api/ApiConstant";
import { selectMappedUserType } from "../../Controllers/selector";
import { publicURL } from "../../Utils/Common";
import { Skeleton } from "primereact/skeleton";

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const dispatch = useAppDispatch();

    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');

    const [showErrorMsg, setShowErrorMsg] = useState<string>('');
    const [emailError, setEmailErrorMsg] = useState<string>('');
    const [passwordError, setPasswordErrorMsg] = useState<string>('');

    const [checked, setChecked] = useState<boolean>(false);
    const [showLoginInfoModal, setShowLoginInfoModal] = useState<boolean>(false);

    useEffect(() => {
        const savedEmail = localStorage.getItem('email');
        const savedPassword = localStorage.getItem('password');
        if (savedEmail) {
            setEmail(savedEmail);
            setChecked(true);
        }
        if (savedPassword) {
            setPassword(savedPassword);
        }

        /* ***** remove local storage data  *******/
        if (localStorage.getItem('token')) {
            localStorage.removeItem('token');
        }
        if (localStorage.getItem('user_id')) {
            localStorage.removeItem('user_id');
        }
        if (localStorage.getItem('user_type_id')) {
            localStorage.removeItem('user_type_id');
        }
        if (localStorage.getItem('user_type_name')) {
            localStorage.removeItem('user_type_name');
        }
        if (localStorage.getItem('company_name')) {
            localStorage.removeItem('company_name');
        }
        if (localStorage.getItem('currency_symbol')) {
            localStorage.removeItem('currency_symbol');
        }
        if (localStorage.getItem('material_tax_percentage')) {
            localStorage.removeItem('material_tax_percentage');
        }
        if (localStorage.getItem('vat_percentage')) {
            localStorage.removeItem('vat_percentage');
        }
        /* ***** remove local storage data  *******/
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(email, password)) {
            setLoading(true);
            const data = await loginService(email, password);

            if (data.status) {
                setShowErrorMsg('');

                if (checked) {
                    localStorage.setItem('email', email);
                    localStorage.setItem('password', password);
                } else {
                    localStorage.removeItem('email');
                    localStorage.removeItem('password');
                }

                const ownerLogined = data.body?.owner_logined;
                if (!ownerLogined) {
                    handleShowUserTypeInfo();
                    setLoading(false);
                    return;
                }

                localStorage.setItem('token', data.body?.token || '');
                localStorage.setItem('login_email', email);
                localStorage.setItem('user_id', String(data.body?.user_id) || '');
                localStorage.setItem('user_type_id', String(data.body?.user_type_id) || '');
                localStorage.setItem('user_type_name', String(data.body?.user_type_name) || '');
                localStorage.setItem('company_name', String(data.body?.company_name) || '');
                localStorage.setItem('currency_symbol', String(data.body?.currency_symbol) || '');
                localStorage.setItem('material_tax_percentage', String(data.body?.material_tax_percentage) || '');
                localStorage.setItem('vat_percentage', String(data.body?.vat_percentage) || '');

                login(data.body?.token || '')
                goToTop();
                setLoading(false);

                // Reset all redux state
                dispatch({ type: 'RESET' });

                const { getAllPermissionsThunk } = await import('../../Controllers/Thunks/permissionThunks');
                dispatch(getAllPermissionsThunk(Number(data.body?.user_type_id)));

                navigate('/dash-board');
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: data.error,
                        life: 3000
                    });
                }
                setLoading(false);
                setShowErrorMsg(data.error || 'Login failed.')
            }
        } else {
            setLoading(false);
            setShowErrorMsg('');
        }
    }

    function checkCredentials(email: string, password: string): boolean {
        const isEmailValid = checkEmail(email, true, setEmailErrorMsg);
        const isPasswordValid = checkText(password, true, setPasswordErrorMsg);

        return isEmailValid && isPasswordValid;
    }

    /* For user type */
    const [showUserTypeModal, setShowUserTypeModal] = useState<boolean>(false);
    const mappedUserTypes: UserType[] = useAppSelector(selectMappedUserType)
    const userTypeError = useAppSelector((state) => state.userTypes.error);
    const loadingUserType = useAppSelector((state) => state.userTypes.loading);
    const [selectedUserType, setSelectedUserType] = useState<UserType | null>(null);
    const [userTypeSubmitLoading, setUserTypeSubmitLoading] = useState<boolean>(false);
    const loadingItems = Array.from({ length: 5 });

    useEffect(() => {
        dispatch(fetchUserTypes());
    }, [dispatch]);

    const handleShowUserTypeInfo = () => {
        setShowUserTypeModal(true);
    }

    const handleCloseUserTypeModal = () => {
        setShowUserTypeModal(false);
    }

    const handleSelectUserType = (userType: UserType) => {
        setSelectedUserType(prevUserType => prevUserType === userType ? null : userType);
    }


    /* ********************  Set User type for contact create user ********************** */
    const handleSubmitUserType = async (e: React.FormEvent) => {
        e.preventDefault();
        setUserTypeSubmitLoading(true);
        try {
            if (selectedUserType?.id) {
                const data = await newUserUserTypeAdd(email, selectedUserType?.id);
                if (data.statusCode === 200) {
                    setTimeout(() => {
                        setUserTypeSubmitLoading(false);
                        handleCloseUserTypeModal();

                        if (toast.current) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Your user type has been successfully updated, and you are now logged into your account.',
                                life: 3000
                            });
                        }
                        handleSubmit(e);
                    }, 500);
                } else {
                    setUserTypeSubmitLoading(false);
                    if (toast.current) {
                        toast.current.show({
                            severity: 'error',
                            summary: 'Failed',
                            detail: 'There was an error creating certificate.',
                            life: 3000
                        });
                    }
                }
            }
        } catch (error) {
            setUserTypeSubmitLoading(false);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating your user type.',
                    life: 3000
                });
            }
        }
    }
    /* ********************  Set User type for contact create user ********************** */

    const userTypeModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select user type
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseUserTypeModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const userTypeModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseUserTypeModal}
            />

            <Button
                label={`${userTypeSubmitLoading ? 'Processing' : 'Submit'}`}
                onClick={handleSubmitUserType}
                loading={userTypeSubmitLoading}
                className="custom_btn primary"
                disabled={selectedUserType === null}
            />
        </div>
    )


    return (
        <React.Fragment>
            <Toast ref={toast} />

            <section className="page_initial_section auth_init_section bg-white">
                <img src="/assets/images/page_dec_img.svg" className="page_dec_img" alt="" />
                <img src="/assets/images/page_dec_img.svg" className="page_dec_img image_2" alt="" />
                <div className="container-md">
                    <div className="row">
                        <div className="col-12 col-xl-7 col-lg-6 mx-auto my-auto sticky_top">
                            <Zoom triggerOnce>
                                <div className="page_image_area">
                                    <img src="/assets/images/traqdo_logo.svg" className="page_logo_img" alt="Traqdo" />
                                    <img src="/assets/images/page-image.svg" className="page_image" alt="Traqdo" />
                                </div>
                            </Zoom>
                        </div>

                        <div className="col-11 col-xl-5 col-lg-6 col-md-10 col-sm-10 mx-auto">
                            <Slide direction="up" triggerOnce>
                                <article className="page_card h-100">
                                    <h2 className="page_card_head">Welcome back</h2>
                                    <h5 className="page_card_sub_head">Log In to your account</h5>
                                    <p className="page_card_desc">Enter your email and password below</p>

                                    <form onSubmit={handleSubmit} className="page_form_area">
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="custom_form_group">
                                                    <label htmlFor="email" className="custom_form_label">Email</label>
                                                    <InputText
                                                        id="email"
                                                        type="email"
                                                        value={email}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                                        className="custom_form_input"
                                                        placeholder="johndeo@gmail.com"
                                                    />

                                                    {emailError && (
                                                        <small className="form_error_msg">{emailError}</small>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="custom_form_group">
                                                    <label htmlFor="passwrord" className="custom_form_label">Password</label>
                                                    <Password
                                                        value={password}
                                                        inputId="password"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                                        feedback={false}
                                                        toggleMask
                                                        tabIndex={1}
                                                        className="custom_form_input w-100"
                                                        placeholder="***********"
                                                    />

                                                    {passwordError && (
                                                        <small className="form_error_msg">{passwordError}</small>
                                                    )}
                                                </div>
                                            </div>

                                            {showErrorMsg && (
                                                <small className="form_error_msg text-center">{showErrorMsg}</small>
                                            )}

                                            <div className="col-12">
                                                <div className="custom_form_action_area">
                                                    <div className="custom_form_check_group">
                                                        <Checkbox inputId="remeberMe" onChange={e => setChecked(e.checked ?? false)} checked={checked}></Checkbox>
                                                        <label htmlFor="remeberMe" className="custom_form_check_label">Remember me</label>
                                                    </div>
                                                    <Link to={'/auth/forgot-password'} className="custom_form_link">
                                                        Forgot Password?
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="custom_form_btn_area">
                                                    <Button
                                                        label="LOG IN"
                                                        loading={loading}
                                                        className="custom_form_btn letter_spaced w-100"
                                                        type="submit"
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="custom_form_bottom_area">
                                                    <p>Don't have an account yet? &nbsp;
                                                        <Link to={'/pricing'}>
                                                            Sign up
                                                        </Link>
                                                    </p>
                                                </div>
                                            </div>

                                        </div>
                                    </form>
                                </article>
                            </Slide>
                        </div>
                    </div>
                </div>
            </section>

            {/* Login info modal */}
            <Dialog headerClassName="d-none" dismissableMask
                visible={showLoginInfoModal} className="custom_modal_dialog modal_dialog_md"
                onHide={() => { if (!showLoginInfoModal) return; setShowLoginInfoModal(false); }}>
                <div className="custom_modal_body">
                    <button className="custom_modal_close position-absolute float p-ripple"
                        onClick={() => setShowLoginInfoModal(false)}>
                        <i className="bi bi-x-lg"></i>
                        <Ripple />
                    </button>

                    <div className="use_case_feature_info">
                        <h6 className="review_head">Access Alert!</h6>

                        <div className="message_alert_area">
                            <img src="/assets/images/alert_image_2.svg" alt="Login Alert!" />
                            <div className="w-100">
                                <p className="review_paragraph text-center">
                                    The web application currently supports login access only for <b>Architect role</b>. Logins for other roles are available through the <b>Mobile app</b>.
                                </p>
                                <p className="review_paragraph text-center">
                                    Applications for other roles on the web platform are under development and will be available soon.
                                    For now, please use the <b>Mobile app</b> to access functionalities for other roles.
                                </p>
                            </div>

                            <div className="mobile_apps_link_area">
                                <Link target="_blank" to={appStoreLink} className="mb_app_link p-ripple">
                                    <img src="/assets/images/website/home-page/app_store.svg" alt="App Store" loading="lazy" />
                                    <Ripple />
                                </Link>
                                <Link target="_blank" to={playStoreLink} className="mb_app_link p-ripple">
                                    <img src="/assets/images/website/home-page/play_store.svg" alt="Play Store" loading="lazy" />
                                    <Ripple />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* User type modal */}
            <Dialog
                visible={showUserTypeModal}
                headerClassName="custom_modal_header"
                header={userTypeModalHeader}
                footer={userTypeModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseUserTypeModal}
                dismissableMask
                closeOnEscape={false}
                resizable={false}
                draggable={false}
            >
                <div className="custom_modal_body pb-0">
                    <div className="p-0 w-100">
                        <Slide direction="down" className="w-100" duration={800}>
                            <article className="user_type_info">
                                <img src={`${publicURL}/assets/images/error/user_error.svg`} alt="" />
                                <p>
                                    It looks like you haven't selected your <span>user type</span>. <br />
                                    To log into your account, select your user type and complete your registration.
                                </p>
                            </article>
                        </Slide>

                        {userTypeError ? (
                            <div className="no_records_found mt-3">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                {userTypeError}
                            </div>
                        ) : (
                            <React.Fragment>
                                {loadingUserType ? (
                                    <div className="user_types_area mt-3">
                                        {loadingItems.map((_, index) => (
                                            <Skeleton
                                                key={index}
                                                className="loading_user_type_item"
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <React.Fragment>
                                        {mappedUserTypes && mappedUserTypes?.length > 0 ? (
                                            <Fade duration={500}>
                                                <div className="user_types_area mt-3">
                                                    {mappedUserTypes?.map((userType) => {
                                                        return (
                                                            <article
                                                                key={userType?.id}
                                                                className={`user_type_item ${userType?.id === selectedUserType?.id ? 'selected' : ''} p-ripple`}
                                                                onClick={() => handleSelectUserType(userType)}
                                                            >
                                                                <img src={userType?.logo ? (baseURL + userType?.logo) : (publicURL + '/assets/images/user.png')} alt="" />
                                                                <h6>{userType?.typeName}</h6>
                                                                <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                            </article>
                                                        )
                                                    })}
                                                </div>
                                            </Fade>
                                        ) : (
                                            <div className="no_records_found mt-3">
                                                <i className="bi bi-exclamation-circle me-1"></i>
                                                No user type(s) found.
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}

        </React.Fragment>
    )
}

export default Login;
